import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import QRCode from 'qrcode';
import { API_BASE_URL } from '../config';
import generateAvatar from '../utils/generateAvatar';
import '../styles/BadgePrint.css';

const BadgePrint = () => {
  const { userId: routeUserId } = useParams();
  const [user, setUser] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get userId from route params
  const userId = routeUserId;

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId || userId === 'badge-print') {
        setError('No user ID provided');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/api/user/${userId}`);
        if (!response.ok) {
          setError('User not found');
          setLoading(false);
          return;
        }
        const data = await response.json();

        if (data) {
          setUser(data);

          // Generate QR code linking to public portfolio
          const portfolioUrl = `https://komok.org/portfolio/${data.id}`;

          QRCode.toDataURL(portfolioUrl, {
            width: 200,
            margin: 1,
            color: {
              dark: '#000000',
              light: '#ffffff'
            },
            errorCorrectionLevel: 'M'
          })
            .then(dataUrl => setQrDataUrl(dataUrl))
            .catch(err => console.error('QR generation error:', err));

        } else {
          setError('User not found');
        }
      } catch (err) {
        console.error('Error fetching user:', err);
        setError('Failed to load user data');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [userId]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="badge-print-container">
        <div className="loading">Loading employee data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="badge-print-container">
        <div className="error">{error}</div>
        <p>URL format: /badge-print/[userId]</p>
      </div>
    );
  }

  return (
    <div className="badge-print-container">
      {/* Print controls - hidden when printing */}
      <div className="print-controls no-print">
        <h2>Badge Print Preview</h2>
        <p>User: {user.username} ({user.shortId})</p>
        <button onClick={handlePrint} className="print-btn">
          Print Badge (4x6)
        </button>
        <p className="print-hint">
          Ensure printer is set to 4x6 label size
        </p>
      </div>

      {/* The actual badge - 4x6 inches */}
      <div className="badge-label">
        {/* Top decorative bar */}
        <div className="badge-top-bar">
          <div className="bar-pattern"></div>
        </div>

        {/* Company header */}
        <div className="badge-header">
          <img src="/komok_logo.png" alt="KOMOK" className="badge-logo" />
        </div>

        {/* Profile picture */}
        <div className="badge-photo-container">
          <img
            src={user.profilePicture
              ? (user.profilePicture.startsWith('data:')
                  ? user.profilePicture
                  : `${API_BASE_URL}${user.profilePicture}`)
              : generateAvatar(user.username)}
            alt="Profile"
            className="badge-photo"
          />
        </div>

        {/* Username */}
        <div className="badge-username">@{user.username}</div>

        {/* Divider */}
        <div className="badge-divider">
          <span className="divider-text">EMPLOYEE IDENTIFICATION</span>
        </div>

        {/* Employee Number */}
        <div className="badge-employee-id">
          <span className="id-label">NO. </span>
          <span className="id-value">#{String(user.employeeNumber || 0).padStart(4, '0')}</span>
        </div>

        {/* QR Code for login */}
        <div className="badge-qr-section">
          {qrDataUrl && (
            <img src={qrDataUrl} alt="Login QR" className="badge-qr" />
          )}
          <div className="qr-label">SCAN TO VIEW PORTFOLIO</div>
        </div>

        {/* Bottom tagline */}
        <div className="badge-footer">
          <div className="footer-warning">KOMOK - OUR PARTY IS YOUR PROFIT</div>
        </div>

        {/* Bottom decorative bar */}
        <div className="badge-bottom-bar">
          <div className="bar-pattern"></div>
        </div>
      </div>
    </div>
  );
};

export default BadgePrint;
